import { createMemo, createSignal, For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { SleepReportScope, SleepTableScope, SleepCandidateScope } from 'mormaii-web-presentation/sleep-report'
import type { SleepBand, SleepPhase } from 'mormaii-web-presentation/sleep-report'

import { Button, TextFilter } from '../../../components/filters'
import { CompanyPicker } from '../../../components/company-picker'
import { PeriodLimitNote, PeriodPicker } from '../../../components/period-picker'
import { Menu, Panel, Spinner } from '../../../components/ui'

import Css from './sleep-report.module.scss'

/**
 * O relatorio de sono: seis tabelas do mesmo grupo de atletas, num periodo.
 *
 * As cores das celulas sao as do relatorio que o cliente JA USA - azul claro para o
 * que esta bom, amarelo para atencao, rosa para ruim. Nao sao as faixas de status do
 * resto do painel, e nao devem virar: quem le este relatorio le o dele ha anos, e a
 * cor e o vocabulario que ele conhece.
 */

/** A partir de quantas linhas a tabela desenha so a janela visivel (B-44). */
const VIRTUAL_FROM = 80

/** Linhas a mais acima e abaixo da janela, para a rolagem nao mostrar o vazio. */
const OVERSCAN = 12

/**
 * Os avisos, do CODIGO que a apresentacao publica para o texto.
 *
 * O scope manda `message = 'SEM_EMPRESA'` e quem escreve a frase e a view - ao
 * contrario do resto do projeto, onde texto de produto mora no scope (ver a landing
 * e a pagina de exclusao de conta). A consequencia apareceu agora: a copia esta em
 * DOIS renderizadores, e uma correcao de texto tem de ser feita duas vezes. Fica
 * espelhado aqui para nao divergir hoje, e anotado para virar campo de scope.
 */

const NOTICES: Record<string, { title: string; body: string }> = {
    SEM_EMPRESA: {
        title: 'Escolha uma empresa',
        body: 'Os atletas pertencem a uma empresa, e um relatório é de um time. Escolha a empresa para montar o grupo.'
    },
    SEM_SELECAO: {
        title: 'Comece escolhendo os atletas',
        body: 'Monte um grupo e salve com um nome. Ele fica guardado para as próximas semanas.'
    },
    SEM_ATLETAS: {
        title: 'Nenhum colaborador para mostrar',
        body: 'Esta empresa ainda não tem colaboradores com acesso ao relatório.'
    },
    NINGUEM_MARCADO: {
        title: 'Ninguém marcado',
        body: 'Abra "Montar seleção" e marque quem deve entrar no relatório.'
    },
    SELECAO_VAZIA: {
        title: 'Esta seleção está vazia',
        body: 'Abra a escolha de atletas e marque quem deve entrar no relatório.'
    },
    SEM_DADOS: {
        title: 'Nenhuma medição no período',
        body: 'Não há noites registradas para estes atletas no período escolhido. Experimente um período maior.'
    },
    ERRO: {
        title: 'Não foi possível carregar',
        body: 'Houve um erro ao buscar as medições. Tente de novo em instantes.'
    }
}

const BANDS: Record<SleepBand, string> = {
    bom: Css.good,
    atencao: Css.watch,
    ruim: Css.bad
}

/**
 * A cor de cada fase do sono, a MESMA do aplicativo.
 *
 * Copiadas de `ViewSleepView.kt`, onde moram como campos privados da view - a paleta
 * do app (`MormaiiColors.kt`) nao as tem, porque ela colapsou laranja, teal e roxo
 * num acento cyan so, e estas quatro sao o unico lugar em que o app ainda distingue
 * cores por significado.
 *
 * Aqui sao TRES: a quarta do app, ACORDADO (`#FF8F66`), nao tem destino no
 * relatorio. A tabela "acordou" e a HORA em que a pessoa acordou, e nao a fase
 * acordado da noite - parecem par e nao sao.
 *
 * ## Por que a cor da fase nao entra na celula
 *
 * A celula ja e pintada por FAIXA DE QUALIDADE (`BANDS` acima), que e a leitura que
 * o relatorio existe para dar. Fase e qualidade sao dimensoes diferentes e disputam
 * o mesmo pixel; por isso a fase fica na ABA e no TITULO, que e onde se escolhe e se
 * confirma o que se esta vendo, e a celula continua respondendo "essa noite foi boa
 * ou ruim".
 */
const PHASES: Record<SleepPhase, string> = {
    rem: '#34E0D2',
    profundo: '#4A9FE7',
    leve: '#8FD9F2'
}

export function SleepReportView(props: ViewProps<SleepReportScope>): JSX.Element {
    return (
        <div class={Css.screen} aria-busy={props.scope.loading}>
            <Controls scope={props.scope} />

            <Show when={props.scope.picking}>
                <Picker scope={props.scope} />
            </Show>

            <Show when={props.scope.message}>{(message) => <Notice code={message()} />}</Show>

            <Show when={props.scope.table}>
                {(table) => (
                    <div class={Css.body}>
                        <Tabs scope={props.scope} />
                        <SleepTable scope={table()} />
                    </div>
                )}
            </Show>
        </div>
    )
}

/**
 * Quem, de quando a quando, e o botao que baixa o PDF.
 *
 * A faixa ESCURA e a mesma do painel, e de proposito: as duas telas fazem a mesma
 * coisa - reunir os filtros que valem para tudo o que esta abaixo. Separar o que
 * CONTROLA do que e mostrado sem precisar de borda e o que a faixa faz; tendo duas
 * telas com o mesmo papel e desenhos diferentes, quem usa aprende duas vezes.
 */
function Controls(props: { scope: SleepReportScope }): JSX.Element {
    return (
        <section class={Css.banner}>
            <div class={Css.haloTop} />
            <div class={Css.haloBottom} />

            <div class={Css.bannerBody}>
                <div class={Css.header}>
                    <div class={Css.titles}>
                        <h2 class={Css.bannerTitle}>{props.scope.selectionName || 'Relatório de sono'}</h2>
                        <p class={Css.subtitle}>
                            <Show
                                when={props.scope.selectionSize > 0}
                                fallback="Escolha a empresa e monte o grupo de atletas"
                            >
                                {props.scope.selectionSizeLabel} · {props.scope.period?.rangeLabel}
                            </Show>
                        </p>
                    </div>

                    <div class={Css.commands}>
                        <GlassButton
                            disabled={props.scope.message === 'SEM_EMPRESA'}
                            onClick={() => void props.scope.onTogglePicking()}
                        >
                            {props.scope.picking ? 'Fechar seleção' : 'Montar seleção'}
                        </GlassButton>

                        <Show when={props.scope.selectionId}>
                            <GlassButton onClick={() => void props.scope.onDeleteSelection()}>
                                Apagar seleção
                            </GlassButton>
                        </Show>

                        <Button
                            disabled={!props.scope.table || props.scope.downloading}
                            onClick={() => void props.scope.onDownload()}
                        >
                            <Show when={props.scope.downloading} fallback={<DownloadIcon />}>
                                <Spinner class={Css.icon} />
                            </Show>
                            {props.scope.downloading ? 'Gerando…' : 'Baixar PDF'}
                        </Button>
                    </div>
                </div>

                {/*
                    Os DOIS PRIMEIROS crescem, e os outros ficam do tamanho que
                    precisam.

                    Empresa e elenco tem rotulo de comprimento imprevisivel - uma
                    razao social nao cabe em largura fixa -, enquanto o periodo e as
                    datas tem largura conhecida. Dar o excedente a quem precisa dele e
                    o que evita a razao social truncada ao lado de um campo de data
                    com espaco sobrando.
                */}
                <div class={Css.bannerFilters}>
                    <Show when={props.scope.companySelector}>
                        {(selector) => <CompanyPicker scope={selector()} variant="glass" class={Css.bannerCompany} />}
                    </Show>

                    {/*
                        O elenco fica SEMPRE na linha, mesmo sem nenhuma selecao
                        salva - desligado, dizendo que nao ha. Sumindo, a linha se
                        rearranjava sozinha na primeira selecao criada, e o campo
                        aparecia num lugar que o leitor nao estava olhando.
                    */}
                    <Menu
                        onDark
                        class={Css.bannerSquad}
                        disabled={props.scope.selections.length === 0}
                        summary={selectionSummary(props.scope)}
                        options={props.scope.selections.map((option) => ({
                            label: option.label,
                            current: option.active,
                            onSelect: option.onSelect
                        }))}
                    />

                    <Show when={props.scope.period}>
                        {(period) => <PeriodPicker scope={period()} onDark class={Css.bannerPeriod} />}
                    </Show>
                </div>

                <Show when={props.scope.period}>{(period) => <PeriodLimitNote scope={period()} onDark />}</Show>
            </div>
        </section>
    )
}

/** O que o seletor de grupo mostra fechado. */
function selectionSummary(scope: SleepReportScope): string {
    if (scope.selections.length === 0) {
        return 'Nenhum grupo salvo'
    }

    return scope.selections.find((option) => option.active)?.label ?? 'Escolha o grupo'
}

/** Um botao de acao sobre a faixa escura. */
function GlassButton(props: { children: JSX.Element; disabled?: boolean; onClick: () => void }): JSX.Element {
    return (
        <button type="button" disabled={props.disabled} onClick={props.onClick} class={Css.glassButton}>
            {props.children}
        </button>
    )
}

function DownloadIcon(): JSX.Element {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
            class={Css.icon}
        >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
    )
}

/** O painel de montar a selecao: busca, marcacao e o nome com que ela e salva. */
function Picker(props: { scope: SleepReportScope }): JSX.Element {
    let nameField: HTMLInputElement | undefined

    return (
        <Panel title="Quem entra no relatório">
            <div class={Css.builder}>
                <div class={Css.row}>
                    <TextFilter
                        label="Buscar atleta"
                        placeholder="Nome"
                        value={props.scope.search}
                        onChange={(value) => props.scope.onSearchChanged(value)}
                    />

                    <label class={Css.field}>
                        <span class={Css.label}>Nome da seleção</span>
                        <div class={Css.squadNaming}>
                            <input
                                ref={nameField}
                                value={props.scope.selectionName}
                                placeholder="Ex.: Elenco profissional"
                                class={Css.squadNameInput}
                            />
                            <Button
                                disabled={props.scope.saving || props.scope.checkedCount === 0}
                                onClick={() => void props.scope.onSaveSelection(nameField?.value ?? '')}
                            >
                                {props.scope.saving ? 'Salvando…' : 'Salvar'}
                            </Button>
                        </div>
                    </label>
                </div>

                <div class={Css.tally}>
                    <p class={Css.tallyText}>
                        {props.scope.checkedCount} de {props.scope.candidates.length} marcados
                    </p>
                    <div class={Css.tallyActions}>
                        <Button variant="ghost" onClick={() => void props.scope.onSelectAll()}>
                            Marcar todos
                        </Button>
                        <Button variant="ghost" onClick={() => void props.scope.onClearAll()}>
                            Desmarcar todos
                        </Button>
                    </div>
                </div>

                <div class={Css.candidates}>
                    <For
                        each={props.scope.candidates}
                        fallback={<p class={Css.noCandidate}>Nenhum atleta encontrado</p>}
                    >
                        {(candidate) => <Candidate scope={candidate} />}
                    </For>
                </div>
            </div>
        </Panel>
    )
}

function Candidate(props: { scope: SleepCandidateScope }): JSX.Element {
    return (
        <label class={Css.candidate}>
            <input
                type="checkbox"
                checked={props.scope.checked}
                onChange={() => props.scope.onToggle()}
                class={Css.checkbox}
            />
            <span class={Css.candidateName}>{props.scope.name}</span>
        </label>
    )
}

/**
 * As seis abas.
 *
 * Uma tabela por vez na tela; as outras cinco existem no scope e so o PDF as
 * percorre - carregar seis matrizes de quatro mil celulas no DOM para mostrar uma
 * seria pagar seis vezes pelo que se ve.
 */
function Tabs(props: { scope: SleepReportScope }): JSX.Element {
    return (
        <div class={Css.tabs}>
            <For each={props.scope.tabs}>
                {(tab) => (
                    <button
                        type="button"
                        onClick={() => void tab.onSelect()}
                        aria-current={tab.active ? 'true' : undefined}
                        class={Css.tab}
                        classList={{ [Css.current]: tab.active }}
                    >
                        {/*
                            O ponto so aparece nas tres abas que nomeiam uma fase.
                            
                            O anel e BRANCO E SOLIDO na aba ativa, e nao um contorno
                            translucido: a aba ativa e roxa e a fase PROFUNDO tambem e
                            (`#7A6BD6`), entao um contorno fraco deixava o ponto virar
                            um anel vazado - lia-se a borda, nao a cor. O anel fica
                            por FORA do ponto, para a cor nao perder area.
                        */}
                        <Show when={tab.phase}>
                            {(phase) => (
                                <span
                                    aria-hidden="true"
                                    class={Css.phaseDot}
                                    style={{ 'background-color': PHASES[phase()] }}
                                />
                            )}
                        </Show>
                        {tab.label}
                    </button>
                )}
            </For>

            {/*
                Girar a tabela (B-36): com muitos atletas, um por coluna deixa a tabela
                larga demais; um por linha cresce para baixo. O botao diz o que ele FAZ.
            */}
            <button
                type="button"
                class={Css.layoutToggle}
                aria-pressed={props.scope.layout === 'atletas'}
                onClick={() => void props.scope.onToggleLayout()}
            >
                {props.scope.layout === 'noites' ? 'Um atleta por linha' : 'Uma noite por linha'}
            </button>
        </div>
    )
}

/**
 * Uma das seis tabelas.
 *
 * A primeira coluna - a data - fica PRESA na borda esquerda: com vinte e um atletas
 * a tabela rola bastante, e sem a data ancorada nao se sabe de que noite e a celula
 * que se esta olhando.
 */
function SleepTable(props: { scope: SleepTableScope }): JSX.Element {
    /*
     * PAGINACAO VIRTUAL (B-44, aprovada em 16/10/2026): com centenas de linhas, so as
     * visiveis vao ao DOM. Medido antes: 2.000 atletas eram 70 mil celulas, e cada
     * troca de aba travava a tela ~1,9 s. Abaixo de `VIRTUAL_FROM` linhas a tabela
     * desenha tudo, como sempre - a janela so paga a conta quando ha conta.
     *
     * E coisa de DESENHO, e por isso mora aqui: a apresentacao entrega as linhas
     * todas, e quantas cabem na tela depende do tamanho da tela.
     */
    const [scrollTop, setScrollTop] = createSignal(0)
    const [viewport, setViewport] = createSignal(600)
    const [rowHeight, setRowHeight] = createSignal(37)

    const virtual = () => props.scope.rows.length > VIRTUAL_FROM

    const slice = createMemo(() => {
        const total = props.scope.rows.length
        if (!virtual()) {
            return { start: 0, end: total }
        }
        const height = rowHeight()
        const start = Math.max(0, Math.floor(scrollTop() / height) - OVERSCAN)
        const end = Math.min(total, Math.ceil((scrollTop() + viewport()) / height) + OVERSCAN)
        return { start, end }
    })

    const visible = () => props.scope.rows.slice(slice().start, slice().end)

    const onScroll = (event: Event) => {
        const box = event.currentTarget as HTMLElement
        setScrollTop(box.scrollTop)
        setViewport(box.clientHeight)
    }

    return (
        <Panel title={props.scope.title} accent={props.scope.phase ? PHASES[props.scope.phase] : undefined} flush>
            <div class={Css.scroller} classList={{ [Css.scrollerTall]: virtual() }} onScroll={onScroll}>
                <table class={Css.table}>
                    <thead classList={{ [Css.stickyHead]: virtual() }}>
                        <tr class={Css.tableHead}>
                            <th class={Css.stuckHead}>{props.scope.corner}</th>
                            <For each={props.scope.columns}>
                                {(column) => (
                                    <th class={Css.column}>
                                        {/*
                                            O nome do atleta ABRE a ficha dele. As pessoas
                                            sao as colunas deste relatorio, entao e aqui que
                                            se clica num individuo.
                                        */}
                                        <Show
                                            when={column.selectable}
                                            fallback={<span class={Css.athlete}>{column.name}</span>}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => void column.onSelect()}
                                                title={`Abrir a ficha de ${column.name}`}
                                                class={Css.athlete}
                                            >
                                                {column.name}
                                            </button>
                                        </Show>
                                    </th>
                                )}
                            </For>
                        </tr>
                    </thead>

                    <tbody>
                        {/* O espaco das linhas de cima que nao estao no DOM. */}
                        <Show when={slice().start > 0}>
                            <tr aria-hidden="true" style={{ height: `${slice().start * rowHeight()}px` }} />
                        </Show>
                        <For each={visible()}>
                            {(row) => (
                                <tr
                                    class={Css.tableRow}
                                    ref={(element) => {
                                        // A altura de uma linha de verdade, para a janela.
                                        queueMicrotask(() => {
                                            if (element.offsetHeight > 0) setRowHeight(element.offsetHeight)
                                        })
                                    }}
                                >
                                    <th class={Css.night}>
                                        {/* Na disposicao por atleta, o nome na linha abre a ficha. */}
                                        <Show when={row.selectable} fallback={row.label}>
                                            <button
                                                type="button"
                                                onClick={() => void row.onSelect()}
                                                title={`Abrir a ficha de ${row.label}`}
                                                class={Css.rowAthlete}
                                            >
                                                {row.label}
                                            </button>
                                        </Show>
                                    </th>
                                    <For each={row.cells}>
                                        {(cell) => (
                                            <td
                                                class={Css.cell}
                                                classList={{ [cell.band ? BANDS[cell.band] : '']: !!cell.band }}
                                            >
                                                {cell.text}
                                            </td>
                                        )}
                                    </For>
                                </tr>
                            )}
                        </For>
                        <Show when={slice().end < props.scope.rows.length}>
                            <tr
                                aria-hidden="true"
                                style={{ height: `${(props.scope.rows.length - slice().end) * rowHeight()}px` }}
                            />
                        </Show>
                    </tbody>
                </table>
            </div>

            <div class={Css.legend}>
                <For each={props.scope.legend}>
                    {(entry) => (
                        <span class={Css.legendItem}>
                            <span class={`${Css.legendSwatch} ${BANDS[entry.band]}`} />
                            <span class={Css.legendName}>{entry.label}</span>
                        </span>
                    )}
                </For>
            </div>
        </Panel>
    )
}

/** O aviso do estado em que a tela esta - ver a tabela `NOTICES` acima. */
function Notice(props: { code: string }): JSX.Element {
    const notice = () => NOTICES[props.code]

    return (
        <div class={Css.notice}>
            <p class={Css.noticeTitle}>{notice()?.title ?? 'Não foi possível montar o relatório'}</p>
            <p class={Css.noticeText}>{notice()?.body ?? props.code}</p>
        </div>
    )
}
